import { forwardRef, useImperativeHandle, useState } from "react";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {
	CardContent,
	Checkbox,
	Container,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useToast } from "@packsize/toast";
import { useToken } from "@packsize/authentication";

import { useServerSettings, useUpdateServerSettings } from "@/hooks/use-server-settings";
import { useTenantTimezone } from "@/hooks/use-tenant-timezone";
import { Loading } from "@/components/loading";
import { FailedRetry } from "@/components/failed-retry";
import type { ServerSettings } from "@/api/server-settings";
import { useCopyToClipboard } from "@/hooks";

interface ServerSettingsTabProps {
	readonly setIsDirty: (isDirty: boolean) => void;
}

export interface ServerSettingsTabRef {
	save: () => Promise<void>;
}

export const ServerSettingsTab = forwardRef<ServerSettingsTabRef, ServerSettingsTabProps>(({ setIsDirty }, ref) => {
	const intl = useIntl();
	const { toastSuccess, toastError } = useToast();
	const { decodedToken } = useToken();

	const copyToClipboard = useCopyToClipboard();

	const customerId = decodedToken?.CustomerId;
	const tenantId = decodedToken?.tid;
	const tenantName = decodedToken?.tname;

	const { data: serverSettings, isLoading, isError, refetch } = useServerSettings();
	const { data: tenantTimezone } = useTenantTimezone();
	const { mutate: updateSettings } = useUpdateServerSettings();

	const [localSettings, setLocalSettings] = useState<ServerSettings | null>(null);

	const settings = localSettings ?? serverSettings;

	useImperativeHandle(
		ref,
		() => ({
			save: async (): Promise<void> => {
				if (!settings) return;
				try {
					await updateSettings(settings);
					setIsDirty(false);
					toastSuccess({ message: intl.formatMessage({ id: "Saved", defaultMessage: "Saved" }) });
				} catch (e) {
					console.error(e);
					toastError({ message: intl.formatMessage({ id: "Failed to save", defaultMessage: "Failed to save" }) });
				}
			},
		}),
		[intl, settings, toastError, toastSuccess, updateSettings, setIsDirty],
	);

	if (isLoading) {
		return <Loading />;
	}

	if (isError) {
		return <FailedRetry retry={refetch} />;
	}

	if (!settings) {
		return null;
	}

	return (
		<CardContent>
			<Stack spacing={3}>
				<Typography variant="h2">
					<FormattedMessage id="General Settings" defaultMessage="General Settings" />
				</Typography>

				<Container disableGutters maxWidth="sm">
					<Stack spacing={3}>
						<TextField
							label={intl.formatMessage({ id: "Site Name", defaultMessage: "Site Name" })}
							value={tenantName ?? intl.formatMessage({ id: "Default", defaultMessage: "Default" })}
							disabled
							slotProps={{
								input: {
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												type="button"
												onClick={() => copyToClipboard(tenantName ?? "")}
												data-cy="settings-tenant-name-clipboard-button"
												data-testid="server-settings__button__copy-tenant-name"
											>
												<ContentCopyOutlinedIcon sx={{ fontSize: 20 }} />
											</IconButton>
										</InputAdornment>
									),
								},
							}}
							fullWidth
							data-cy="settings-tenant-name"
							data-testid="server-settings__input__tenant-name"
						/>

						<TextField
							label={intl.formatMessage({ id: "Site Id", defaultMessage: "Site Id" })}
							value={tenantId ? tenantId : intl.formatMessage({ id: "Default", defaultMessage: "Default" })}
							disabled
							slotProps={{
								input: {
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												type="button"
												onClick={() => copyToClipboard(tenantId ?? "")}
												data-cy="settings-company-clipboard-button"
											>
												<ContentCopyOutlinedIcon sx={{ fontSize: 20 }} />
											</IconButton>
										</InputAdornment>
									),
								},
							}}
							fullWidth
							data-cy="settings-company"
						/>

						<TextField
							label={intl.formatMessage({ id: "Customer Id", defaultMessage: "Customer Id" })}
							value={customerId ?? intl.formatMessage({ id: "Default", defaultMessage: "Default" })}
							disabled
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												type="button"
												onClick={() => copyToClipboard(customerId ?? "")}
												data-cy="settings-company-clipboard-button"
											>
												<ContentCopyOutlinedIcon sx={{ fontSize: 20 }} />
											</IconButton>
										</InputAdornment>
									),
								},
							}}
							fullWidth
							data-cy="settings-customer-id"
						/>
						<TextField
							label={intl.formatMessage({ id: "Site Time Zone", defaultMessage: "Site Time Zone" })}
							value={tenantTimezone?.siteTimeZone ?? ""}
							disabled
							fullWidth
							data-cy="settings-site-time-zone"
						/>

						<FormControl fullWidth>
							<InputLabel>
								<FormattedMessage id="Unit of Measure" defaultMessage="Unit of Measure" />
							</InputLabel>
							<Select
								fullWidth
								label={intl.formatMessage({ id: "Unit of Measure", defaultMessage: "Unit of Measure" })}
								value={settings.unitOfMeasure === "inches" ? "inches" : "mm"}
								onChange={(event: SelectChangeEvent<string>) => {
									setLocalSettings({ ...settings, unitOfMeasure: event.target.value });
									setIsDirty(true);
								}}
								data-cy="settings-unit-of-measurement"
								data-testid="settings-unit-of-measurement"
							>
								<MenuItem value="inches">
									<FormattedMessage id="Inches" defaultMessage="Inches" />
								</MenuItem>
								<MenuItem value="mm">
									<FormattedMessage id="Millimeters" defaultMessage="Millimeters" />
								</MenuItem>
							</Select>
						</FormControl>

						<FormControl fullWidth>
							<InputLabel>
								<FormattedMessage id="User Logout Expiration" defaultMessage="User Logout Expiration" />
							</InputLabel>
							<Select
								fullWidth
								label={<FormattedMessage id="User Logout Expiration" defaultMessage="User Logout Expiration" />}
								value={settings.loginExpirationInHours}
								onChange={(event) => {
									setLocalSettings({ ...settings, loginExpirationInHours: Number(event.target.value) });
									setIsDirty(true);
								}}
								data-cy="settings-logout-expiration"
							>
								<MenuItem value={12}>
									<FormattedMessage id="12 Hours" defaultMessage="12 Hours" />
								</MenuItem>
								<MenuItem value={24}>
									<FormattedMessage id="24 Hours" defaultMessage="24 Hours" />
								</MenuItem>
							</Select>
						</FormControl>

						{Boolean(settings.ssoDomain) === true && (
							<FormControlLabel
								control={
									<Checkbox
										data-testid="disable-password-login-toggle"
										checked={settings.disablePasswordLogin}
										onChange={(_, checked) => {
											setIsDirty(true);
											setLocalSettings({
												...settings,
												disablePasswordLogin: checked,
											});
										}}
									/>
								}
								label={intl.formatMessage({
									id: "Disable Password Login",
									defaultMessage: "Disable Password Login",
								})}
							/>
						)}
					</Stack>
				</Container>
			</Stack>
		</CardContent>
	);
});

ServerSettingsTab.displayName = "ServerSettingsTab";
